import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, X, Eye, EyeOff, Check, Upload } from "lucide-react";
import { contentList, contentCreate, contentUpdate, contentDelete, contentUpload } from "../adminApi";
import { resolveMedia } from "@/lib/contentApi";

// Generic content manager for gallery / blog / testimonials / events.
export default function AdminContent({ kind, title, note, fields, toggle, display }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setItems((await contentList(kind)).items); }
    catch { toast.error("Failed to load."); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [kind]);

  const doToggle = async (it) => {
    const cur = it[toggle.field];
    const next = toggle.field === "status" ? (cur === "published" ? "draft" : "published") : !cur;
    try { await contentUpdate(kind, it.id, { [toggle.field]: next }); load(); toast.success("Updated."); }
    catch { toast.error("Update failed."); }
  };
  const remove = async () => {
    try { await contentDelete(kind, confirmDel.id); setConfirmDel(null); load(); toast.success("Deleted."); }
    catch { toast.error("Delete failed."); }
  };
  const isOn = (it) => toggle.field === "status" ? it.status === "published" : !!it[toggle.field];

  return (
    <div data-testid={`admin-${kind}`}>
      <div className="mb-6 flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-slate-900">{title}</h1><p className="text-sm text-slate-500">{note}</p></div>
        <button onClick={() => setEditing({})} data-testid={`${kind}-add`} className="inline-flex items-center gap-2 rounded-lg bg-orange px-4 py-2 text-sm font-medium text-white"><Plus className="h-4 w-4" /> Add</button>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange" /></div> : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400">No items yet. Click &quot;Add&quot; to create one.</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`${kind}-item`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-3">
                {(it.featuredImage || it.photo || it.thumbnail || it.url || it.image) && (it.type !== "video") && (
                  <img src={resolveMedia(it.featuredImage || it.photo || it.thumbnail || it.url || it.image)} alt="" className="h-12 w-12 rounded-lg object-cover" />
                )}
                <div>
                  <div className="font-semibold text-slate-900">{display(it)}</div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className={`rounded-full px-2 py-0.5 font-semibold ${isOn(it) ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{isOn(it) ? toggle.on : toggle.off}</span>
                    {it.category && <span>{it.category}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => doToggle(it)} data-testid={`${kind}-toggle`} className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50" title="Toggle visibility">
                  {isOn(it) ? <Eye className="h-4 w-4" /> : (toggle.field === "approved" ? <Check className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />)}
                </button>
                <button onClick={() => setEditing(it)} data-testid={`${kind}-edit`} className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setConfirmDel(it)} data-testid={`${kind}-delete`} className="rounded-lg border border-slate-200 p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && <EditModal kind={kind} fields={fields} record={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmDel(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <Trash2 className="mx-auto mb-3 h-10 w-10 text-red-500" />
            <h3 className="text-lg font-bold text-slate-900">Delete this item?</h3>
            <p className="mb-5 text-sm text-slate-500">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDel(null)} className="flex-1 rounded-lg border border-slate-300 py-2 text-sm font-medium">Cancel</button>
              <button onClick={remove} data-testid={`${kind}-delete-confirm`} className="flex-1 rounded-lg bg-red-500 py-2 text-sm font-medium text-white">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditModal({ kind, fields, record, onClose, onSaved }) {
  const [f, setF] = useState(record.id ? record : {});
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      if (record.id) await contentUpdate(kind, record.id, f);
      else await contentCreate(kind, f);
      toast.success("Saved.");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid={`${kind}-modal`}>
        <div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-bold text-slate-900">{record.id ? "Edit" : "Add"} {kind}</h2><button onClick={onClose}><X className="h-5 w-5 text-slate-500" /></button></div>
        <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">Tip: you can upload files directly or paste a public URL. For images, compressing first (e.g. &lt;300KB, WebP/JPG) keeps the site fast. Use only media you own or have permission to use.</p>
        <div className="flex flex-col gap-3">
          {fields.map((fd) => (
            <label key={fd.k} className="flex flex-col gap-1.5 text-sm text-slate-600">{fd.label}
              {fd.type === "select" ? (
                <select value={f[fd.k] || ""} onChange={set(fd.k)} data-testid={`${kind}-field-${fd.k}`} className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange">
                  <option value="">Select</option>{fd.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : fd.type === "textarea" ? (
                <textarea value={f[fd.k] || ""} onChange={set(fd.k)} rows={fd.rows || 3} data-testid={`${kind}-field-${fd.k}`} className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
              ) : fd.type === "file" ? (
                <FileField field={fd} kind={kind} value={f[fd.k]} onChange={(v) => setF((s) => ({ ...s, [fd.k]: v }))} />
              ) : (
                <input type={fd.type || "text"} value={f[fd.k] || ""} onChange={set(fd.k)} data-testid={`${kind}-field-${fd.k}`} className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
              )}
            </label>
          ))}
        </div>
        <button onClick={save} disabled={saving} data-testid={`${kind}-save`} className="mt-5 w-full rounded-lg bg-orange py-2.5 font-medium text-white disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}

function FileField({ field, kind, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const isVideo = field.media === "video" || /\.(mp4|webm|mov|m4v)$/i.test(value || "");
  const isPdf = /\.(pdf)$/i.test(value || "");

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await contentUpload(file);
      onChange(url);
      toast.success("Uploaded.");
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {value && (
        isVideo ? (
          <video src={resolveMedia(value)} controls className="h-32 w-auto rounded-lg bg-black" />
        ) : isPdf ? (
          <a href={resolveMedia(value)} target="_blank" rel="noopener noreferrer" className="w-fit text-xs font-medium text-orange underline">View uploaded document</a>
        ) : (
          <img src={resolveMedia(value)} alt="" className="h-20 w-20 rounded-lg object-cover" />
        )
      )}
      <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50">
        {uploading ? <Loader2 className="h-4 w-4 animate-spin text-orange" /> : <Upload className="h-4 w-4" />}
        {uploading ? "Uploading…" : value ? "Replace file" : "Upload file"}
        <input type="file" accept={field.accept} onChange={handle} disabled={uploading} data-testid={`${kind}-file-${field.k}`} className="hidden" />
      </label>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste a public URL"
        data-testid={`${kind}-field-${field.k}`}
        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs outline-none focus:border-orange"
      />
    </div>
  );
}
